/**
 * The voices a pattern clip should play, as data.
 *
 * A pattern clip carries the same music twice: `noteEvents` at source tick
 * resolution, and a 16th-note `pattern`/`noteData` grid for the sequencer to
 * draw. The engine prefers the events whenever they exist, because the grid
 * cannot express a triplet roll — several rolled notes collapse onto one step
 * and the extra ones are simply lost.
 *
 * That preference used to be written out separately everywhere a pattern was
 * played, and offline export implemented only the grid half. So an exported
 * mix quietly flattened every roll that the user could hear during playback.
 * Deciding *which voices exist* is a pure question, so it is answered once
 * here; callers keep the part that is genuinely theirs — building nodes on
 * their own audio context.
 */

const DEFAULT_PPQ = 96;
const BEATS_PER_STEP = 0.25; // the grid is 16th notes

const clamp01 = (value, fallback = 1) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.max(0, Math.min(1, num));
};

/**
 * @param {object} clipData         the clip's pattern payload
 * @param {object} options
 * @param {number} options.maxBeats voices at or past this offset are dropped,
 *                                  so a shortened clip does not play material
 *                                  beyond its own length
 * @returns {Array<{drumIndex, beatOffset, pitch, velocity, durationBeats}>}
 *          sorted by beatOffset, then drumIndex, so scheduling order is stable
 *          and lane cut-self behaviour is deterministic
 */
export function patternVoices(clipData = {}, options = {}) {
  const maxBeats = Number.isFinite(options.maxBeats) ? options.maxBeats : Infinity;
  const ppq = Number(clipData?.ppq) > 0 ? Number(clipData.ppq) : DEFAULT_PPQ;
  const voices = [];

  const events = Array.isArray(clipData?.noteEvents) ? clipData.noteEvents : null;

  if (events && (events.length > 0 || clipData?.noteEventsAuthoritative)) {
    events.forEach((event) => {
      if (event?.muted) return;
      const drumIndex = Number(event?.drumIndex);
      if (!Number.isFinite(drumIndex) || drumIndex < 0) return;

      const startTick = Math.max(0, Number(event?.startTick) || 0);
      const beatOffset = startTick / ppq;
      if (!(beatOffset < maxBeats)) return;

      const pitches = Array.isArray(event?.pitches)
        ? [...new Set(event.pitches.map(Number).filter(Number.isFinite))] : [];
      const fallbackPitch = Number.isFinite(event?.pitch) ? Number(event.pitch) : null;
      // Pitched browser one-shots are polyphonic sequencer instruments. Drum
      // rows remain single-voice even if malformed data happens to list more.
      const voicePitches = clipData?.drumSounds?.[drumIndex]?.playbackMode === 'oneshot'
        ? (pitches.length ? pitches : [fallbackPitch])
        : [fallbackPitch ?? (pitches.length ? Math.min(...pitches) : null)];

      const durationTicks = Number(event?.durationTicks);
      const playablePitches = clipData?.drumSounds?.[drumIndex]?.playbackMode === 'oneshot'
        ? voicePitches.filter(Number.isFinite) : voicePitches;
      playablePitches.forEach((pitch) => {
        voices.push({
          drumIndex,
          beatOffset,
          pitch,
          velocity: clamp01(event?.velocity),
          durationBeats: Number.isFinite(durationTicks) && durationTicks > 0
            ? durationTicks / ppq
            : BEATS_PER_STEP
        });
      });
    });
  } else {
    const pattern = clipData?.pattern || {};
    const noteData = clipData?.noteData || {};

    Object.keys(pattern).forEach((key) => {
      if (!pattern[key]) return;
      // Keys are exactly `${drumIndex}-${step}`. Splitting without checking the
      // shape is a trap: "-1-0" yields ["", "1", "0"], and Number("") is 0, so a
      // malformed negative lane would silently render as lane 0.
      const parts = String(key).split('-');
      if (parts.length !== 2 || parts[0] === '' || parts[1] === '') return;
      const drumIndex = Number(parts[0]);
      const step = Number(parts[1]);
      if (!Number.isInteger(drumIndex) || !Number.isInteger(step)) return;
      if (drumIndex < 0 || step < 0) return;

      const beatOffset = step * BEATS_PER_STEP;
      if (!(beatOffset < maxBeats)) return;

      const info = noteData[key] || {};
      const durationSteps = Number(info.durationSteps);
      const polyphonic = clipData?.drumSounds?.[drumIndex]?.playbackMode === 'oneshot';
      const pitches = polyphonic && Array.isArray(info.pitches) ? info.pitches : [info.pitch];
      const playablePitches = polyphonic
        ? [...new Set(pitches.map(Number).filter(Number.isFinite))]
        : [Number.isFinite(info.pitch) ? Number(info.pitch) : null];
      playablePitches.forEach((pitch) => {
        voices.push({
          drumIndex,
          beatOffset,
          pitch,
          velocity: clamp01(info.velocity),
          durationBeats: (Number.isFinite(durationSteps) && durationSteps > 0 ? durationSteps : 1) * BEATS_PER_STEP
        });
      });
    });
  }

  voices.sort((a, b) => a.beatOffset - b.beatOffset || a.drumIndex - b.drumIndex);
  return voices;
}

/** True when the clip carries high-resolution timing the grid cannot express. */
export function hasHighResolutionTiming(clipData = {}) {
  return Array.isArray(clipData?.noteEvents) && clipData.noteEvents.length > 0;
}

export default patternVoices;
